import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt  = (n, d=2) => n != null && n !== '' ? Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d}) : '—'
const fmtP = (n) => n != null ? (n>=0?'+':'')+Number(n).toFixed(1)+'%' : '—'

const XIcon      = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const PlusIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const TrashIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
const UploadIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
const DownIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${type==='error'?'bg-red-600':'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

const ConfirmOverlay = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg">Delete</button>
      </div>
    </div>
  </div>
)

const EMPTY_ROW = { sub_code: '', activity_type: '', entry_date: new Date().toISOString().split('T')[0], equipment_desc: '', qty_executed: '', notes: '' }

export default function EquipmentProductivity({ initProject }) {
  const [projects, setProjects]               = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [entries, setEntries]                 = useState([])
  const [budgetRows, setBudgetRows]           = useState([])
  const [loading, setLoading]                 = useState(false)
  const [toast, setToast]                     = useState(null)
  const [confirmDelete, setConfirmDelete]     = useState(null)

  // Rate per equipment-day — per sub_code, entered by user
  const [rates, setRates]                     = useState({}) // { sub_code: rate }
  const [globalRate, setGlobalRate]           = useState('')  // fallback global rate

  // Filters
  const [subCodeFilter, setSubCodeFilter]     = useState('all')
  const [dateFrom, setDateFrom]               = useState('')
  const [dateTo, setDateTo]                   = useState('')

  // Entry modal
  const [showModal, setShowModal]             = useState(false)
  const [showRateModal, setShowRateModal]     = useState(false)
  const [rateForm, setRateForm]               = useState({}) // { sub_code: rate }
  const [editEntry, setEditEntry]             = useState(null)
  const [form, setForm]                       = useState({ ...EMPTY_ROW })

  // Upload
  const [uploadPreview, setUploadPreview]     = useState(null)
  const [uploading, setUploading]             = useState(false)
  const fileRef                               = useRef(null)

  const showToast = (msg, type='success') => setToast({ message: msg, type })
  const h = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const getRate = (sc) => parseFloat(rates[sc]) || parseFloat(globalRate) || 0

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data||[]))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadEntries(selectedProject.id)
      loadBudget(selectedProject.id)
      loadEquipmentRates(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadEquipmentRates = async (pid) => {
    const { data } = await supabase.from('equipment_rates').select('sub_code, rate').eq('project_id', pid)
    const map = {}
    ;(data || []).forEach(r => { map[r.sub_code] = parseFloat(r.rate) })
    setRates(map)
  }

  const saveAllRates = async () => {
    if (!selectedProject?.id) return
    const entries = Object.entries(rateForm).filter(([, r]) => parseFloat(r) > 0)
    if (!entries.length) return showToast('ادخل rate واحد على الأقل', 'error')
    await Promise.all(entries.map(([sub_code, rate]) =>
      supabase.from('equipment_rates').upsert(
        { project_id: selectedProject.id, sub_code, rate: parseFloat(rate) },
        { onConflict: 'project_id,sub_code' }
      )
    ))
    const newRates = { ...rates }
    entries.forEach(([sc, r]) => { newRates[sc] = parseFloat(r) })
    setRates(newRates)
    showToast('تم حفظ الـ rates')
    setShowRateModal(false)
  }

  const loadEntries = useCallback(async (pid) => {
    setLoading(true)
    const { data } = await supabase
      .from('equipment_productivity_entries')
      .select('*')
      .eq('project_id', pid)
      .order('entry_date', { ascending: false })
    setEntries(data || [])
    setLoading(false)
  }, [])

  const loadBudget = async (pid) => {
    const { data } = await supabase
      .from('tender_direct_items')
      .select('budget_material_code, labour_code, budget_sc_code, budget_equipment_code, row_type, description, resource_description, qty, unit_rate, total_budget, labour_total')
      .eq('project_id', pid)
      .in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
    const map = {}
    ;(data||[]).forEach(r => {
      let sc = null
      if (r.row_type === 'labour')           sc = r.labour_code
      else if (r.row_type === 'material')    sc = r.budget_material_code
      else if (r.row_type === 'subcontract') sc = r.budget_sc_code
      else if (r.row_type === 'equipment')   sc = r.budget_equipment_code
      if (!sc) return
      if (!map[sc]) map[sc] = { sub_code: sc, description: r.resource_description || r.description || '', budget_total: 0, budget_qty: 0, budget_rate: 0 }
      const rowTotal = r.row_type === 'labour' ? (parseFloat(r.labour_total) || parseFloat(r.total_budget) || 0) : (parseFloat(r.total_budget) || 0)
      map[sc].budget_total += rowTotal
      map[sc].budget_qty   += parseFloat(r.qty) || 0
      if (r.unit_rate) map[sc].budget_rate = parseFloat(r.unit_rate)
    })
    setBudgetRows(Object.values(map))
  }

  // ── Save entry ────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.sub_code)     return showToast('Sub Code مطلوب', 'error')
    if (!form.entry_date)   return showToast('التاريخ مطلوب', 'error')
    if (!form.equipment_desc) return showToast('وصف المعدة مطلوب', 'error')
    if (!form.qty_executed) return showToast('الكمية المنفذة مطلوبة', 'error')

    const payload = {
      project_id:     selectedProject.id,
      sub_code:       form.sub_code.trim(),
      activity_type:  form.activity_type.trim() || null,
      entry_date:     form.entry_date,
      equipment_desc: form.equipment_desc.trim(),
      qty_executed:   parseFloat(form.qty_executed) || 0,
      notes:          form.notes.trim() || null,
    }

    const { error } = editEntry
      ? await supabase.from('equipment_productivity_entries').update(payload).eq('id', editEntry.id)
      : await supabase.from('equipment_productivity_entries').insert(payload)

    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(editEntry ? 'تم التحديث' : 'تم الحفظ')
    setShowModal(false)
    loadEntries(selectedProject.id)
  }

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await supabase.from('equipment_productivity_entries').delete().eq('id', id)
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadEntries(selectedProject.id)
  }

  // ── Download Template ─────────────────────────────────────
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['entry_date', 'sub_code', 'activity_type', 'equipment_desc', 'qty_executed', 'notes'],
      ['2026-01-05', 'Dir.Eq.Dew', 'Dewatering', 'Pump Unit 1', 24, ''],
      ['2026-01-06', 'Dir.Eq.Dew', 'Dewatering', 'Pump Unit 1', 24, ''],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Log')
    XLSX.writeFile(wb, 'equipment_productivity_template.xlsx')
  }

  // ── Parse & Upload Excel ──────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
      const preview = rows.map(r => ({
        entry_date:     String(r.entry_date     || r['Date']            || r['DATE']            || '').trim(),
        sub_code:       String(r.sub_code       || r['Sub Code']        || r['SUB CODE']        || '').trim(),
        activity_type:  String(r.activity_type  || r['Activity Type']   || r['ACTIVITY']         || '').trim() || null,
        equipment_desc: String(r.equipment_desc || r['Equipment Desc']  || r['EQUIPMENT']        || '').trim(),
        qty_executed:   parseFloat(r.qty_executed || r['Qty Executed'] || r['QTY'] || 0) || 0,
        notes:          String(r.notes || r['Notes'] || '').trim() || null,
      })).filter(r => r.entry_date && r.sub_code && r.equipment_desc && r.qty_executed > 0)
      setUploadPreview(preview)
    }
    reader.readAsArrayBuffer(file)
  }

  const handleConfirmUpload = async () => {
    if (!uploadPreview?.length) return
    setUploading(true)
    const payload = uploadPreview.map(r => ({ ...r, project_id: selectedProject.id }))
    const BATCH = 100
    let ok = 0
    for (let i = 0; i < payload.length; i += BATCH) {
      const { error } = await supabase.from('equipment_productivity_entries').insert(payload.slice(i, i + BATCH))
      if (!error) ok += Math.min(BATCH, payload.length - i)
    }
    setUploading(false)
    showToast(`تم رفع ${ok} سجل`)
    setUploadPreview(null)
    loadEntries(selectedProject.id)
  }

  // ── Filter entries ────────────────────────────────────────
  const filtered = entries.filter(e => {
    if (subCodeFilter !== 'all' && e.sub_code !== subCodeFilter) return false
    if (dateFrom && e.entry_date < dateFrom) return false
    if (dateTo   && e.entry_date > dateTo)   return false
    return true
  })

  // ── Log rows (sorted asc) with cumulative per sub_code ──
  const logRows = [...filtered].sort((a,b) => a.entry_date.localeCompare(b.entry_date)).map((e, i, arr) => {
    const sc   = e.sub_code
    const rate = getRate(sc)
    const prev = arr.slice(0, i).filter(r => r.sub_code === sc)
    const cum_qty    = prev.reduce((s,r) => s + (parseFloat(r.qty_executed)||0), 0) + (parseFloat(e.qty_executed)||0)
    const cum_days   = prev.length + 1
    const cum_cost   = cum_days * rate
    const day_cost   = rate
    const actual_rate = cum_qty > 0 ? cum_cost / cum_qty : 0
    const budget     = budgetRows.find(b => b.sub_code === sc)
    const budget_rate = budget?.budget_rate || (budget?.budget_qty > 0 ? budget.budget_total / budget.budget_qty : 0)
    return { ...e, rate, day_cost, cum_qty, cum_days, cum_cost, actual_rate, budget_rate }
  })

  // ── Summary per sub_code ──────────────────────────────────
  const summaryMap = {}
  logRows.forEach(e => {
    if (!summaryMap[e.sub_code]) summaryMap[e.sub_code] = { sub_code: e.sub_code, total_days: 0, total_qty: 0 }
    summaryMap[e.sub_code].total_days += 1
    summaryMap[e.sub_code].total_qty  += parseFloat(e.qty_executed) || 0
  })
  const summary = Object.values(summaryMap).map(s => {
    const rate         = getRate(s.sub_code)
    const total_cost   = s.total_days * rate
    const actual_rate  = s.total_qty > 0 ? total_cost / s.total_qty : 0
    const budget       = budgetRows.find(b => b.sub_code === s.sub_code)
    const budget_rate  = budget?.budget_rate || (budget?.budget_qty > 0 ? budget.budget_total / budget.budget_qty : 0)
    const budget_total = budget?.budget_total || 0
    const rate_var     = budget_rate > 0 ? actual_rate - budget_rate : null
    const rate_var_pct = budget_rate > 0 ? (rate_var / budget_rate) * 100 : null
    const cost_var     = budget_total > 0 ? total_cost - budget_total : null
    return { ...s, rate, total_cost, actual_rate, budget_rate, budget_total, budget_qty: budget?.budget_qty||0, rate_var, rate_var_pct, cost_var, description: budget?.description||'' }
  })

  const uniqueSubCodes = [...new Set(entries.map(e => e.sub_code))].sort()

  if (!selectedProject) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-[#1C1A18] mb-6">Equipment Productivity</h1>
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center">
          <p className="text-sm text-[#6B7280] mb-4">اختر مشروع</p>
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            {projects.map(p => (
              <button key={p.id} onClick={() => setSelectedProject(p)}
                className="text-left p-4 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#1B3A6B]">
                <div className="font-semibold text-[#1C1A18] text-sm">{p.name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
      {confirmDelete && (
        <ConfirmOverlay
          message="حذف هذا السجل؟"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1C1A18]">Equipment Productivity</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{selectedProject.name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadTemplate} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
            <DownIcon/> Template
          </button>
          <label className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#1B3A6B]/5 cursor-pointer">
            <UploadIcon/> Upload Log
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => { if(e.target.files[0]) handleFileSelect(e.target.files[0]) }}/>
          </label>
          <button onClick={() => { setRateForm({...rates}); setShowRateModal(true) }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs border border-amber-300 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100">
            ⚙ Set Rates
          </button>
          <button onClick={() => { setEditEntry(null); setForm({...EMPTY_ROW}); setShowModal(true) }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90">
            <PlusIcon/> Add Entry
          </button>
        </div>
      </div>

      {/* Upload Preview */}
      {uploadPreview && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-[#1C1A18]">Preview — {uploadPreview.length} سجل</span>
            <div className="flex gap-2">
              <button onClick={() => setUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg">Cancel</button>
              <button onClick={handleConfirmUpload} disabled={uploading}
                className="px-3 py-1.5 text-xs bg-[#1B3A6B] text-white rounded-lg disabled:opacity-60">
                {uploading ? 'Uploading...' : `✓ Confirm Upload (${uploadPreview.length})`}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-48">
            <table className="w-full text-xs">
              <thead className="bg-[#1B3A6B] text-white">
                <tr>
                  {['Date','Sub Code','Activity','Equipment','Qty Executed'].map(hh => (
                    <th key={hh} className="px-3 py-2 text-left">{hh}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {uploadPreview.slice(0,20).map((r,i) => (
                  <tr key={i} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-1.5 font-mono text-[#6B7280]">{r.entry_date}</td>
                    <td className="px-3 py-1.5 font-mono font-semibold text-[#1B3A6B]">{r.sub_code}</td>
                    <td className="px-3 py-1.5 text-[#6B7280]">{r.activity_type||'—'}</td>
                    <td className="px-3 py-1.5 text-[#6B7280]">{r.equipment_desc}</td>
                    <td className="px-3 py-1.5 text-right">{r.qty_executed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={subCodeFilter} onChange={e => setSubCodeFilter(e.target.value)}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none text-[#6B7280]">
          <option value="all">All Sub Codes</option>
          {uniqueSubCodes.map(sc => <option key={sc} value={sc}>{sc}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none text-[#6B7280]"/>
        <span className="text-[#9CA3AF] text-xs">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none text-[#6B7280]"/>
        {(subCodeFilter !== 'all' || dateFrom || dateTo) && (
          <button onClick={() => { setSubCodeFilter('all'); setDateFrom(''); setDateTo('') }}
            className="text-xs text-[#9CA3AF] hover:text-[#1C1A18]">✕ Clear</button>
        )}
        <span className="text-xs text-[#9CA3AF] ml-auto">{filtered.length} entries</span>
      </div>

      {/* ── Summary per Sub Code ── */}
      {summary.length > 0 && (parseFloat(globalRate) > 0 || Object.values(rates).some(r => parseFloat(r) > 0)) && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide">Summary by Sub Code</h2>
          {summary.map(s => {
            const over = s.rate_var != null && s.rate_var > 0
            const bRate = s.budget_rate
            return (
              <div key={s.sub_code} className={`bg-white rounded-xl border p-4 ${over ? 'border-red-200' : 'border-[#E5E7EB]'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="font-mono font-bold text-[#1B3A6B]">{s.sub_code}</span>
                    {s.description && <span className="text-xs text-[#6B7280] ml-2">{s.description}</span>}
                    <span className="text-xs text-[#9CA3AF] ml-2">@ {fmt(s.rate)} SAR/equipment-day</span>
                  </div>
                  {s.rate_var != null && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${over ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {over ? '▲ Over' : '▼ Under'} {fmtP(s.rate_var_pct)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-3 text-xs">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-[#6B7280] mb-1">Budget</div>
                    <div className="font-bold text-blue-700">{fmt(s.budget_total, 0)} SAR</div>
                    <div className="text-[#9CA3AF]">{fmt(s.budget_qty)} units</div>
                    <div className="text-[#9CA3AF]">{fmt(bRate)}/unit</div>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <div className="text-[#6B7280] mb-1">Equipment-Days</div>
                    <div className="font-bold text-[#1C1A18] text-lg">{s.total_days}</div>
                    <div className="text-[#9CA3AF]">days</div>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <div className="text-[#6B7280] mb-1">Qty Executed</div>
                    <div className="font-bold text-[#1C1A18] text-lg">{fmt(s.total_qty)}</div>
                    <div className="text-[#9CA3AF]">units</div>
                  </div>
                  <div className={`rounded-lg p-3 ${over ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className="text-[#6B7280] mb-1">Actual Cost</div>
                    <div className={`font-bold ${over ? 'text-red-700' : 'text-[#1A7A4A]'}`}>{fmt(s.total_cost, 0)} SAR</div>
                    <div className="text-[#9CA3AF]">{fmt(s.actual_rate)}/unit</div>
                  </div>
                  <div className={`rounded-lg p-3 ${s.cost_var != null && s.cost_var > 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <div className="text-[#6B7280] mb-1">Variance</div>
                    <div className={`font-bold ${s.cost_var != null && s.cost_var > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                      {s.cost_var != null ? (s.cost_var > 0 ? '+' : '') + fmt(s.cost_var, 0) + ' SAR' : '—'}
                    </div>
                    <div className="text-[#9CA3AF]">Rate: {fmt(s.actual_rate)} vs {fmt(bRate)}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* No rate warning */}
      {summary.length > 0 && !parseFloat(globalRate) && !Object.values(rates).some(r => parseFloat(r) > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
          ⚠️ أدخل Rate/Equipment-Day في الأعلى لتظهر حسابات التكلفة والمقارنة مع البادجت
        </div>
      )}

      {/* ── Log Table ── */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
        <div className="px-4 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1C1A18]">Equipment Log</h2>
          <span className="text-xs text-[#9CA3AF]">Cumulative per Sub Code</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-sm text-[#9CA3AF]">Loading...</div>
        ) : logRows.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#9CA3AF]">
            مفيش بيانات — أضف entries أو ارفع Excel
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Date</th>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Sub Code</th>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Activity</th>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Equipment</th>
                <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Qty Today</th>
                <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Day Cost</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold border-l border-[#E5E7EB]">Cum. Days</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold">Cum. Qty</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold">Cum. Cost</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold">Actual Rate</th>
                <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold border-l border-[#E5E7EB]">Budget Rate</th>
                <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Var %</th>
                <th className="px-3 py-3 text-[#6B7280]">Notes</th>
                <th className="px-3 py-3 w-16"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {logRows.map(e => {
                const hasRate  = e.rate > 0
                const over     = hasRate && e.budget_rate > 0 && e.actual_rate > e.budget_rate
                const varPct   = hasRate && e.budget_rate > 0 ? ((e.actual_rate - e.budget_rate) / e.budget_rate) * 100 : null
                return (
                  <tr key={e.id} className={`hover:bg-[#F9FAFB] ${over ? 'bg-red-50/20' : ''}`}>
                    <td className="px-3 py-2.5 font-mono text-[#6B7280]">{e.entry_date}</td>
                    <td className="px-3 py-2.5 font-mono font-semibold text-[#1B3A6B]">{e.sub_code}</td>
                    <td className="px-3 py-2.5 text-[#6B7280]">{e.activity_type||'—'}</td>
                    <td className="px-3 py-2.5 text-[#6B7280]">{e.equipment_desc||'—'}</td>
                    <td className="px-3 py-2.5 text-right">{fmt(e.qty_executed)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#1C1A18]">
                      {hasRate ? fmt(e.day_cost) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-[#1B3A6B] border-l border-[#F3F4F6]">{e.cum_days}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#1B3A6B]">{fmt(e.cum_qty)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#1B3A6B]">
                      {hasRate ? fmt(e.cum_cost, 0) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-bold ${over ? 'text-red-600' : hasRate ? 'text-[#1A7A4A]' : 'text-[#9CA3AF]'}`}>
                      {hasRate ? fmt(e.actual_rate) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-[#6B7280] border-l border-[#F3F4F6]">
                      {e.budget_rate > 0 ? fmt(e.budget_rate) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {varPct != null ? (
                        <span className={`font-semibold ${over ? 'text-red-600' : 'text-[#1A7A4A]'}`}>
                          {over ? '▲' : '▼'} {fmtP(varPct)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-[#9CA3AF] max-w-xs truncate">{e.notes||'—'}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">
                        <button onClick={() => {
                          setEditEntry(e)
                          setForm({ sub_code: e.sub_code, activity_type: e.activity_type||'', entry_date: e.entry_date, equipment_desc: e.equipment_desc||'', qty_executed: e.qty_executed, notes: e.notes||'' })
                          setShowModal(true)
                        }} className="p-1 text-[#6B7280] hover:text-[#1B3A6B] hover:bg-[#F3F4F6] rounded">✏</button>
                        <button onClick={() => setConfirmDelete(e.id)}
                          className="p-1 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded"><TrashIcon/></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-[#F9FAFB] border-t-2 border-[#E5E7EB] text-xs font-semibold">
              <tr>
                <td colSpan={4} className="px-3 py-3 text-[#6B7280] uppercase">Total</td>
                <td className="px-3 py-3 text-right">{fmt(logRows.reduce((s,e)=>s+(parseFloat(e.qty_executed)||0),0))}</td>
                <td className="px-3 py-3 text-right text-[#1B3A6B]">
                  {parseFloat(globalRate) > 0 || Object.values(rates).some(r=>parseFloat(r)>0)
                    ? fmt(logRows.reduce((s,e)=>s+e.day_cost,0), 0)
                    : '—'}
                </td>
                <td colSpan={8} className="border-l border-[#F3F4F6]"/>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* ── Set Rates Modal ── */}
      {showRateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <div>
                <h2 className="text-base font-bold text-[#1C1A18]">Set Equipment Rates</h2>
                <p className="text-xs text-[#9CA3AF] mt-0.5">Rate per equipment-day (SAR) per sub code</p>
              </div>
              <button onClick={() => setShowRateModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18]"><XIcon/></button>
            </div>
            <div className="p-5 space-y-3">
              {/* Global rate */}
              <div className="bg-[#F9FAFB] rounded-lg p-3 flex items-center gap-3">
                <span className="text-xs font-semibold text-[#6B7280] whitespace-nowrap w-32">Global (default):</span>
                <input
                  type="number"
                  value={globalRate}
                  onChange={e => setGlobalRate(e.target.value)}
                  placeholder="e.g. 800"
                  className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                <span className="text-xs text-[#9CA3AF]">SAR/day</span>
              </div>
              <div className="text-xs text-[#9CA3AF] px-1">Per sub code (overrides global):</div>
              {/* Sub codes from budget */}
              {budgetRows.length > 0 ? budgetRows.map(b => (
                <div key={b.sub_code} className="flex items-center gap-3 p-3 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]">
                  <div className="flex-1">
                    <div className="font-mono text-xs font-semibold text-[#1B3A6B]">{b.sub_code}</div>
                    {b.description && <div className="text-xs text-[#9CA3AF] mt-0.5 truncate">{b.description}</div>}
                    {rates[b.sub_code] && <div className="text-xs text-green-600 mt-0.5">Saved: {rates[b.sub_code]} SAR/day</div>}
                  </div>
                  <input
                    type="number"
                    value={rateForm[b.sub_code] || ''}
                    onChange={e => setRateForm(f => ({ ...f, [b.sub_code]: e.target.value }))}
                    placeholder={rates[b.sub_code] || globalRate || '—'}
                    className="w-28 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                  <span className="text-xs text-[#9CA3AF]">SAR/day</span>
                </div>
              )) : (
                <div className="text-xs text-[#9CA3AF] text-center py-4">لا يوجد sub codes في البادجت</div>
              )}
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowRateModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={saveAllRates} className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">Save Rates</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-base font-bold text-[#1C1A18]">{editEntry ? 'Edit Entry' : 'Add Entry'}</h2>
              <button onClick={() => setShowModal(false)}><XIcon/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Sub Code *</label>
                  <input list="sc-list-eq" value={form.sub_code} onChange={e => h('sub_code', e.target.value)}
                    placeholder="e.g. Dir.Eq.Dew"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 font-mono"/>
                  <datalist id="sc-list-eq">
                    {budgetRows.map(b => <option key={b.sub_code} value={b.sub_code}>{b.sub_code} — {b.description}</option>)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Activity Type</label>
                  <input value={form.activity_type} onChange={e => h('activity_type', e.target.value)}
                    placeholder="e.g. Dewatering"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                </div>
              </div>

              {/* Budget preview */}
              {form.sub_code && (() => {
                const b = budgetRows.find(r => r.sub_code === form.sub_code)
                if (!b) return null
                const bRate = b.budget_rate || (b.budget_qty > 0 ? b.budget_total / b.budget_qty : 0)
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs flex gap-4 flex-wrap">
                    <span className="text-[#6B7280]">Budget:</span>
                    <span className="font-semibold text-blue-700">{fmt(b.budget_total, 0)} SAR</span>
                    <span className="text-[#6B7280]">Qty:</span>
                    <span className="font-semibold text-blue-700">{fmt(b.budget_qty)}</span>
                    <span className="text-[#6B7280]">Budget Rate:</span>
                    <span className="font-semibold text-blue-700">{fmt(bRate)}/unit</span>
                  </div>
                )
              })()}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Date *</label>
                  <input type="date" value={form.entry_date} onChange={e => h('entry_date', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Equipment Description *</label>
                  <input value={form.equipment_desc} onChange={e => h('equipment_desc', e.target.value)}
                    placeholder="e.g. Pump Unit 1"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Qty Executed *</label>
                <input type="number" value={form.qty_executed} onChange={e => h('qty_executed', e.target.value)}
                  placeholder="8.5"
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
              </div>

              {/* Live preview */}
              {form.qty_executed && getRate(form.sub_code) > 0 && (
                <div className="bg-[#F9FAFB] rounded-lg px-4 py-2 text-xs flex gap-4 flex-wrap">
                  <span className="text-[#6B7280]">Day Cost:</span>
                  <span className="font-bold text-[#1B3A6B]">{fmt(getRate(form.sub_code))} SAR</span>
                  <span className="text-[#6B7280]">Rate Today:</span>
                  <span className="font-bold text-[#1B3A6B]">{fmt(getRate(form.sub_code) / (parseFloat(form.qty_executed)||1))}/unit</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Notes</label>
                <input value={form.notes} onChange={e => h('notes', e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">
                {editEntry ? 'Save Changes' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
